import { useState, useEffect, useRef } from 'react';
import MunicipalitiesService from '../services/MunicipalitiesService';

/**
 * Hook para buscar municípios dinamicamente baseado em zoom e bounds.
 * Faz cache por bbox e só dispara quando zoomLevel ≥ threshold.
 * @param {LatLngBounds} bounds - Bounds do mapa (Leaflet)
 * @param {number} zoomLevel - Nível de zoom atual
 * @param {number} [threshold=11] - Zoom mínimo para disparar fetch
 */
export default function useFetchMunicipios(bounds, zoomLevel, threshold = 8) {
  const cacheRef = useRef(new Map());
  const [municipios, setMunicipios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // console.log('useFetchMunicipios: effect', { bounds, zoomLevel, threshold });
    if (!bounds || zoomLevel < threshold) {
      // console.log('useFetchMunicipios: early exit, threshold not met or no bounds', { zoomLevel, threshold });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(false);
      setMunicipios([]);
      return;
    }

    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const bboxKey = `${sw.lng},${sw.lat},${ne.lng},${ne.lat}`;
    //console.log('useFetchMunicipios: bboxKey', bboxKey);

    if (cacheRef.current.has(bboxKey)) {
      //console.log('useFetchMunicipios: using cache for', bboxKey);
      setLoading(false);
      setMunicipios(cacheRef.current.get(bboxKey));
      return;
    }

    // console.log('useFetchMunicipios: starting fetch for', bboxKey);
    setLoading(true);
    MunicipalitiesService.buscarMunicipiosPorBBox(bboxKey)
      .then((data) => {
        // console.log('useFetchMunicipios: raw data', data);
        // Extrai o array de municípios do wrapper de resposta
        const list = Array.isArray(data) ? data : data.data || [];
        // console.log('useFetchMunicipios: extracted list', list);
        cacheRef.current.set(bboxKey, list);
        setMunicipios(list);
        setError(null);
      })
      .catch((err) => {
        // console.error('useFetchMunicipios: error', err);
        setError(err);
      })
      .finally(() => {
        // console.log('useFetchMunicipios: fetch completed');
        setLoading(false);
      });
  }, [bounds, zoomLevel, threshold]);

  return { municipios, loading, error };
}
